import { SearchClientFactory } from "./services/SearchClientFactory";

// Déclaration du type pour les résultats SSR InstantSearch
declare global {
    interface Window {
        __INSTANTSEARCH_INITIAL_RESULTS__?: Record<string, unknown>;
        __INSTANTSEARCH_INITIAL_UI_STATE__?: Record<string, unknown>;
    }
}

// === FIX: Décoder les crochets dans les URLs (Inertia les encode) ===
if (typeof window !== "undefined") {
    const decodeSquareBrackets = (url: string | URL | null | undefined) => {
        if (!url) return url;
        const urlStr = url.toString();
        // Décoder %5B -> [ et %5D -> ]
        return urlStr.replace(/%5B/g, "[").replace(/%5D/g, "]");
    };

    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = function (data, unused, url) {
        return originalPushState(data, unused, decodeSquareBrackets(url));
    };

    history.replaceState = function (data, unused, url) {
        return originalReplaceState(data, unused, decodeSquareBrackets(url));
    };
}
// === FIN FIX ===

import "../css/app.scss";
import "vue-toastification/dist/index.css";
import "./bootstrap";
import "./header/header-mode";
import "./header/header-autocomplete";
import "./header/header-menu";
import "./header/header-dropdown";
import { initCompareFavorite } from "./marketplace/widgets/compare-favorite";
initCompareFavorite();

import env from "./env";
import { createSSRApp, createApp, h, DefineComponent } from "vue";
import { createInertiaApp } from "@inertiajs/vue3";
import VueGoogleMaps from "@fawmi/vue-google-maps";
import { ZiggyVue } from "ziggy-js";
import VueInstantSearch, {
    createServerRootMixin,
} from "vue-instantsearch/vue3/es";
import DefaultLayoutFile from "./Layouts/Default.vue";
import { createI18n } from "vue-i18n";
import { fr } from "./locales/fr";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import Toast, { PluginOptions, POSITION } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import {
    beforeSendSentryEvent,
    sentryDenyUrls,
    sentryPerformanceTracingOptions,
} from "./sentry-client-config";
import { isClientSide, isMobile } from "./helpers/client";
import { prepareTitle } from "./helpers/title";
import mitt from "mitt";
import { createAlgoliaRouting } from "./services/algoliaRouting";

const i18n = createI18n({
    locale: "fr", // set locale
    fallbackLocale: "fr", // set fallback locale
    legacy: false,
    messages: {
        fr,
    },
});

const emitter = mitt();

const options: PluginOptions = {
    position: POSITION.BOTTOM_CENTER,
};

createInertiaApp({
    title: (title) => prepareTitle(title),
    resolve: async (name) => {
        if (!name) {
            throw new Error(
                `Erreur inertia de page inconnue: ${window.location.href}`,
            );
        }

        const pages = import.meta.glob<DefineComponent>("./Pages/**/*.vue", {
            eager: false,
        });

        let pageResolver = pages[`./Pages/${name}.vue`];

        if (!pageResolver) {
            throw new Error(`Page not found: ${name}`);
        }

        const page = await pageResolver(); // <-- ici
        page.default.layout = page.default.layout || DefaultLayoutFile;
        return page;
    },
    setup({ el, App, props, plugin }) {
        const searchClient = SearchClientFactory.create(
            props?.initialPage?.props?.search,
        );

        const cloneComponent2 = function (
            componentInstance,
            { mixins = [] } = {},
        ) {
            const options = {
                serverPrefetch: undefined,
                fetch: undefined,
                _base: undefined,
                name: "ais-ssr-root-component",
            };

            let app;

            const appOptions = Object.assign(
                {},
                componentInstance.$options,
                options,
            );
            appOptions.mixins = [...mixins, ...(appOptions.mixins || [])];
            app = createSSRApp(appOptions);
            if (componentInstance.$router) {
                app.use(componentInstance.$router);
            }
            if (componentInstance.$store) {
                app.use(componentInstance.$store);
            }
            if (componentInstance.$i18n) {
                app.use(componentInstance.$i18n);
            }

            // Important addition here : this is how we can add algolia to the app
            app.use(plugin);
            app.use(ZiggyVue, {
                ...props.initialPage.props.ziggy,
                location: new URL(props.initialPage.props.ziggy.location),
            } as any);
            app.use(i18n);
            app.use(VueInstantSearch);

            // https://stackoverflow.com/a/48195006/3185307
            app.$slots = componentInstance.$slots;
            app.$root = componentInstance.$root;

            return app;
        };

        // NOTE: On ne récupère plus ssrInitialUiState car le routing gère l'état via routeToState
        // Le SSR injecte les résultats de recherche, et le routing parse l'URL pour reconstruire l'état

        const app = createApp({
            mixins: [
                createServerRootMixin({
                    searchClient,
                    indexName: props?.initialPage?.props?.search?.indexName,
                    $cloneComponent: cloneComponent2,
                    future: {
                        preserveSharedStateOnUnmount: false,
                    },
                    routing: createAlgoliaRouting(
                        props?.initialPage?.props?.search?.indexName,
                    ),
                    // NOTE: On ne passe PAS initialUiState quand routing est actif
                    // car le routing lit l'état depuis l'URL via routeToState
                    // Passer les deux causerait un conflit
                    onStateChange: ({ uiState, setUiState }) => {
                        emitter.emit("algolia-state-change", {
                            uiState,
                            setUiState,
                        });
                    },
                }),
            ],
            // Hydrater avec les résultats SSR InstantSearch (pattern officiel Algolia)
            // https://www.algolia.com/doc/guides/building-search-ui/going-further/server-side-rendering/vue/
            beforeMount() {
                if (
                    typeof window === "object" &&
                    window.__INSTANTSEARCH_INITIAL_RESULTS__
                ) {
                    (this as any).instantsearch.hydrate(
                        window.__INSTANTSEARCH_INITIAL_RESULTS__,
                    );

                    // Nettoyer pour éviter une réapplication accidentelle
                    delete window.__INSTANTSEARCH_INITIAL_RESULTS__;
                }
            },
            render: () => h(App, props),
        });

        // Configuration des plugins
        app.use(plugin);
        app.use(ZiggyVue, Ziggy);
        app.use(VueInstantSearch);
        app.use(i18n);
        app.use(Toast, options);
        app.use(VueGoogleMaps, {
            load: {
                key: env.VITE_GOOGLE_API_KEY,
                libraries: "places",
            },
        });

        app.config.globalProperties.$emitter = emitter;

        // Define your global filters
        app.config.globalProperties.$filters = {
            number(value: number) {
                if (isNaN(value)) return value;
                return new Intl.NumberFormat("fr-FR").format(value);
            },
            currency(value: number, strict: boolean = true) {
                return (
                    new Intl.NumberFormat("fr-FR", {
                        minimumFractionDigits: strict ? 0 : 2,
                        maximumFractionDigits: strict ? 0 : 2,
                    }).format(value) + "€"
                );
            },
            date(value, format) {
                return dayjs(value)
                    .locale("fr")
                    .format(format ? format : "DD MMMM YYYY");
            },
        };

        Sentry.init({
            app,
            dsn: env.VITE_SENTRY_DSN_PUBLIC,
            environment: env.VITE_SENTRY_ENVIRONMENT,

            integrations: [Sentry.browserTracingIntegration()],

            tracePropagationTargets: [
                "localhost",
                /^https:\/\/(.+\.)?joinsteer\.com/,
                /^https:\/\/(.+\.)?joinsteer\.fr/,
                /^\/api\//,
            ],

            sampleRate: parseFloat(env.VITE_SENTRY_SAMPLE_RATE || "0.1"),
            ...sentryPerformanceTracingOptions(
                env.VITE_SENTRY_TRACES_SAMPLE_RATE,
            ),

            enabled: env.VITE_SENTRY_ENABLED != "false",

            denyUrls: sentryDenyUrls,
            beforeSend: beforeSendSentryEvent,
        });

        Sentry.setTag(
            "device_type",
            isClientSide() && isMobile() ? "mobile" : "desktop",
        );

        if (isClientSide()) {
            Sentry.setContext("viewport", { width: window.innerWidth });
        }

        app.mount(el);
    },
    progress: {
        color: "#4B5563",
    },
});
